import {
    createStyles,
    Theme,
    Typography,
    withStyles,
    WithStyles
} from "@material-ui/core";
import React from "react";
import NumberFormat from "react-number-format";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { IAppDispatch, IAppState } from "../../../store";
import * as ParcelBidStore from "../../../store/ParcelBidStore";
import * as UserStore from "../../../store/UserStore";

type IProps = ParcelBidStore.IParcelBidStoreState &
    UserStore.IUserStoreState &
    WithStyles<typeof styles> & {
        completed: boolean;
        isLive: boolean;
        lastAcceptedBid?: number;
        parcelId: string;
        startingBid?: number;
        isParcelClosed?: boolean;
        isPrivateSale?: boolean;
        hasAcceptedBids: boolean;
        lastAcceptedBidOverride?: number;
    };
interface BidAmountTextState {
    isHighlight: boolean;
}
export const styles = (theme: Theme) =>
    createStyles({
        bidAmountText: {
            [theme.breakpoints.down("xs")]: { fontSize: 18 },
            color: "white",
            fontWeight: "bold"
        },
        bidAmountTextActive: {
            fontWeight: "bold",
            color: theme.palette.primary.main
        },
        bidAmountTextHighestBidder: { color: theme.palette.success.main },
        bidAmountTextActiveHighlight: {
            color: theme.palette.error.main,
            fontWeight: "bold",
            transition: "color 1000ms linear"
        },
        bidAmountCompleted: {
            color: "#F5F5F5"
        },
        bidAmountWrapper: { display: "inline-block" },
        bidPopup: {
            [theme.breakpoints.down("sm")]: {
                paddingLeft: theme.spacing(3),
                paddingRight: theme.spacing(3)
            },
            display: "inline-block",
            paddingLeft: theme.spacing(),
            paddingRight: theme.spacing()
        },
        bidPopupClosed: {
            [theme.breakpoints.down("xs")]: {
                paddingLeft: `${theme.spacing()}px !important`,
                paddingRight: `${theme.spacing()}px !important`
            },
            paddingLeft: `${theme.spacing(2)}px !important`,
            paddingRight: `${theme.spacing(2)}px !important`
        },
        bidPopupHighest: {
            backgroundColor: theme.palette.success.main,
            color: theme.palette.success.contrastText
        },
        bidStatusText: {
            [theme.breakpoints.down("xs")]: { fontSize: 10 },
            display: "block",
            fontWeight: "bold",
            marginTop: 5,
            color: theme.palette.common.white
        }
    });

class BidAmountText extends React.Component<IProps, BidAmountTextState> {
    bidUpdateTimer: NodeJS.Timeout | null = null;
    constructor(props: IProps) {
        super(props);
        this.state = {
            isHighlight: false
        };
    }
    public shouldComponentUpdate(
        prevProps: IProps,
        prevState: BidAmountTextState
    ) {
        if (
            prevProps.completed !== this.props.completed ||
            prevProps.isLive !== this.props.isLive ||
            prevProps.lastAcceptedBid !== this.props.lastAcceptedBid ||
            prevProps.parcelId !== this.props.parcelId ||
            prevProps.parcelBidDict[prevProps.parcelId] !==
                this.props.parcelBidDict[this.props.parcelId] ||
            prevProps.startingBid !== this.props.startingBid ||
            prevProps.user !== this.props.user ||
            prevProps.hasAcceptedBids !== this.props.hasAcceptedBids ||
            prevState.isHighlight !== this.state.isHighlight
        ) {
            return true;
        }
        return false;
    }
    public componentDidUpdate(prevProps: IProps) {
        if (
            (this.props.hasAcceptedBids && !prevProps.hasAcceptedBids) ||
            (this.props.hasAcceptedBids &&
                this.props.lastAcceptedBid !== prevProps.lastAcceptedBid)
        ) {
            this.highlightText();
        }
    }
    public render() {
        const {
            classes,
            completed,
            isLive,
            isParcelClosed,
            isPrivateSale,
            lastAcceptedBid,
            parcelBidDict,
            parcelId,
            startingBid,
            user,
            lastAcceptedBidOverride: lastAcceptedBIdOverride
        } = this.props;
        const parcelBids = parcelBidDict[parcelId] || [];
        const highestBidder = !!(
            user &&
            parcelBids.length > 0 &&
            user.id === parcelBids[parcelBids.length - 1].userId
        );
        let fontClasses =
            isLive || !completed
                ? this.state.isHighlight
                    ? `${classes.bidAmountTextActive} ${classes.bidAmountText} ${classes.bidAmountTextActiveHighlight}`
                    : `${classes.bidAmountText} ${classes.bidAmountTextActive}`
                : classes.bidAmountText;

        if (highestBidder && isLive) {
            fontClasses = `${fontClasses} ${classes.bidAmountTextHighestBidder}`;
        }
        fontClasses = completed
            ? `${classes.bidAmountCompleted}`
            : !isLive
            ? `${classes.bidAmountText}`
            : `${fontClasses}`;

        const hideClosedParcelAmount =
            isParcelClosed && parcelBids.length === 0 && !isPrivateSale;
        let bidValue =
            !isLive && !completed
                ? startingBid
                : lastAcceptedBIdOverride || lastAcceptedBid || startingBid;
        bidValue = hideClosedParcelAmount ? undefined : bidValue;
        const bidStatus =
            parcelBids.length > 0 ? "Current Bid" : "Starting Bid";

        return (
            <div className={classes.bidAmountWrapper}>
                {highestBidder && !isLive && completed && (
                    <Typography
                        className={`${classes.bidPopup} ${classes.bidPopupHighest} ${classes.bidPopupClosed}`}
                        component="div"
                        variant="caption"
                        display="block"
                    >
                        Highest bidder!
                    </Typography>
                )}
                <Typography
                    component="h5"
                    variant="h5"
                    id={`bid-text-${this.props.parcelId}`}
                    className={fontClasses}
                    display="block"
                >
                    <NumberFormat
                        value={bidValue}
                        thousandSeparator={true}
                        prefix="$"
                        decimalScale={0}
                        fixedDecimalScale={true}
                        displayType="text"
                    />
                    {isLive && (
                        <Typography
                            className={classes.bidStatusText}
                            component="span"
                            variant="caption"
                            color="textPrimary"
                        >
                            {bidStatus}
                        </Typography>
                    )}
                </Typography>
            </div>
        );
    }
    private highlightText() {
        if (this.bidUpdateTimer !== null) {
            clearTimeout(this.bidUpdateTimer);
            // This line removes the highlight class so it can flash the original color and then highlight again
            document
                .getElementById(`bid-text-${this.props.parcelId}`)
                ?.classList.remove(
                    this.props.classes.bidAmountTextActiveHighlight
                );
        }
        this.setState({ isHighlight: true });
        this.bidUpdateTimer = setTimeout(() => {
            this.setState({ isHighlight: false });
            this.bidUpdateTimer = null;
        }, 30000);
    }
}

const reduxState = (state: IAppState) =>
    Object.assign({}, state.parcelBidStoreState, state.userStoreState);
const reduxDispatch = (dispatch: IAppDispatch) =>
    bindActionCreators(
        Object.assign(
            {},
            ParcelBidStore.actionCreators,
            UserStore.actionCreators
        ),
        dispatch
    );

export default withStyles(styles)(
    connect(reduxState, reduxDispatch)(BidAmountText)
);
